import React, { useContext, useEffect, useMemo, useState } from "react";
import { ClassCheckinSheetProps } from "./SheetContent";
import {
  DashboardCheckinSource,
  SelectedMember,
  mapAttendeeToSelectedMember,
} from "./checkinHelpers";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../utils/misc";
import {
  Button,
  SelectTrigger,
  Select,
  SelectContent,
  SelectItem,
  Separator,
  SelectValue,
  Label,
  Skeleton,
} from "@pushpress/pushpress-ui";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "../../../hooks/useNavigate";
import { AppContext } from "../../../context/app";
import {
  useCoachCheckinMutation,
  useCreateReservationMutation,
  useGetCalendarItemDetailQuery,
} from "../../Calendar/CalendarDetail/CalendarDetail-generated.hooks";
import useToggle from "../../../hooks/state";
import AddMemberModal from "../../Calendar/AddMemberModal";
import { filterWaitlist, filterAttendees } from "../../Calendar/Calendar.utils";
import { CheckinMemberCard } from "./CheckinMemberCard";
import { MinimalCalendarItem } from "../../../apis/calendarApi";
import { ClassScheduleItem, useTodaysClasses } from "./useTodaysClasses";
import { useGetSubscriptionsQuery } from "../../../components/controllers/SubscriptionSelect/SusbcriptionSelect-generated.hooks";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { useCheckinMutation } from "../../../apis/calendarApi.hooks";
import {
  useCancelCheckInMutation,
  useCancelReservationMutation,
  useRemoveLateCancelMutation,
} from "../../Calendar/CalendarProvider/CalendarProvider-generated.hooks";

export const ClassCheckInSheetContent: React.FC<ClassCheckinSheetProps> = ({
  data,
}) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [coachCheckInMutation] = useCoachCheckinMutation();
  const checkinMutation = useCheckinMutation();
  const [cancelCheckInMutation] = useCancelCheckInMutation({
    onCompleted: () => {
      trackEvent("Undo Check-in Succeeded");
    },
    onError: (error) => {
      trackEvent("Undo Check-in Failed");
      toast.error(
        (error as Error).message || "Failed to undo check-in",
        defaultToastOptions,
      );
    },
  });
  const [removeLateCancelMutation] = useRemoveLateCancelMutation({
    onCompleted: () => {
      trackEvent("Remove Late Cancel Succeeded");
    },
    onError: (error) => {
      trackEvent("Remove Late Cancel Failed");
      toast.error(
        (error as Error).message || "Failed to remove late cancel",
        defaultToastOptions,
      );
    },
  });
  const [cancelReservationMutation] = useCancelReservationMutation({
    onCompleted: () => {
      trackEvent("Cancel Check-in Reservation Succeeded");
    },
    onError: (error) => {
      trackEvent("Cancel Check-in Reservation Failed");
      toast.error(
        (error as Error).message || "Failed to cancel reservation",
        defaultToastOptions,
      );
    },
  });
  const { classScheduleItem, isMultiClass } = data;
  const [selectedClass, setSelectedClass] = useState<string | null>(
    classScheduleItem?.id,
  );
  const { todaysClasses, todaysClassesLoading } = useTodaysClasses();
  const todaysClass = todaysClasses?.find(
    (cls) => cls.id === selectedClass,
  ) as ClassScheduleItem;

  useEffect(() => {
    if (isMultiClass && todaysClasses?.length > 0 && !selectedClass) {
      setSelectedClass(todaysClasses?.[0]?.id);
    }
  }, [isMultiClass, todaysClasses, selectedClass]);

  const { client } = useContext(AppContext);
  const [showAddMemberModal, showAddMemberModalActions] = useToggle(false);

  const { data: subscriptionData, refetch: refetchSubscriptions } =
    useGetSubscriptionsQuery({
      skip: true,
    });
  const [createReservation] = useCreateReservationMutation({
    onCompleted: () => {
      trackEvent("Check-in Reservation Succeeded");
    },
    onError: (error) => {
      trackEvent("Check-in Reservation Failed");
    },
  });

  const {
    data: calendarItemData,
    loading: calendarItemLoading,
    refetch,
  } = useGetCalendarItemDetailQuery({
    skip: !selectedClass,
    variables: {
      getCalendarItemInput: {
        uuid: selectedClass,
        clientUuid: client.uuid || client.client_uuid,
      },
    },
  });
  const calendarItem = calendarItemData?.getCalendarItem;
  const coachIds = useMemo(
    () =>
      [
        calendarItem?.mainCoach?.userUuid,
        calendarItem?.assistantCoach?.userUuid,
      ].filter((userUuid) => userUuid),
    [calendarItem?.mainCoach?.userUuid, calendarItem?.assistantCoach?.userUuid],
  );
  const { isMainCoachCheckedIn, isAssistantCoachCheckedIn } = useMemo(
    () => ({
      isMainCoachCheckedIn: calendarItemData?.getCalendarItem?.attendees?.some(
        (attendee) =>
          attendee.userUuid ===
          calendarItemData.getCalendarItem?.mainCoach?.userUuid,
      ),
      isAssistantCoachCheckedIn:
        calendarItemData?.getCalendarItem?.attendees?.some(
          (attendee) =>
            attendee.userUuid ===
            calendarItemData?.getCalendarItem?.assistantCoach?.userUuid,
        ),
    }),
    [
      calendarItemData?.getCalendarItem?.attendees,
      calendarItemData?.getCalendarItem?.mainCoach?.userUuid,
      calendarItemData?.getCalendarItem?.assistantCoach?.userUuid,
    ],
  );

  const registrations = useMemo(
    () => ({
      attendees: filterAttendees(calendarItem?.attendees || [], coachIds).map(
        mapAttendeeToSelectedMember,
      ),

      waitlist: filterWaitlist(calendarItem?.attendees || []).map(
        mapAttendeeToSelectedMember,
      ),
    }),
    [calendarItem?.attendees, coachIds],
  );

  const showCoachesSection =
    calendarItem?.mainCoach || calendarItem?.assistantCoach;
  const showWaitlistSection = registrations.waitlist.length > 0;
  const showAttendeesSection = registrations.attendees.length > 0;

  const showCheckinAllButton =
    registrations.attendees.some(
      (attendee) => attendee.status !== "checkedin",
    ) || registrations.waitlist.length > 0;

  const checkIfClassIsFree = (classId: string) => {
    const classDetails: MinimalCalendarItem = todaysClasses.find(
      (cls) => cls.id === classId,
    )?.data as MinimalCalendarItem;
    return classDetails?.price === "0.00";
  };

  const handleCheckin = async (
    member: SelectedMember,
    isCoaching?: boolean,
    isAssisting?: boolean,
  ) => {
    if (!calendarItemData.getCalendarItem) {
      toast.error("Please select a class", defaultToastOptions);
      return;
    }

    try {
      let registrationUuid = member?.registrationUuid;

      // If no existing registration, create one
      if (!registrationUuid) {
        const isFree = checkIfClassIsFree(selectedClass);
        let subscriptionUuid: string | undefined = undefined;

        if (!isFree && (!member.isStaff || member.role === "member")) {
          const { data: subscriptionData } = await refetchSubscriptions({
            getSubscriptionInput: {
              userUuid: member.userUuid,
              calendarItemUuid: selectedClass,
              clientUuid: client.uuid,
              limit: null,
              page: null,
            },
          });

          const subscriptions = subscriptionData?.getSubscriptions;
          const eligibleSubscription = subscriptions?.subscriptions.find(
            (sub) => sub.isEligibleForBooking,
          );
          subscriptionUuid = eligibleSubscription?.subscriptionUuid;
        }

        const createReservationResponse = await createReservation({
          variables: {
            createReservationInput: {
              subscriptionUuid: subscriptionUuid
                ? String(subscriptionUuid)
                : undefined,
              clientUserUuid: member.clientUserUuid,
              calendarItemUuid: selectedClass,
              userUuid: !subscriptionUuid ? member.userUuid : undefined,
              overrideRules: true,
              clientUuid: client.uuid,
              source: DashboardCheckinSource,
            },
          },
        });

        registrationUuid =
          createReservationResponse.data?.createReservation.uuid;
      }

      // Perform check-in
      await checkinMutation.mutateAsync({
        userUuid: member.userUuid,
        registrationUuid,
        shouldOverrideCheckinRules: true,
        source: DashboardCheckinSource,
        isStaff: member.isStaff,
        isCoaching: !!isCoaching,
        isAssisting: !!isAssisting,
      });

      // Refetch calendar item to get updated attendees
      await refetch();

      toast.success(
        `Successfully checked in ${member.firstName} ${member.lastName}`,
        defaultToastOptions,
      );
    } catch (error) {
      toast.error(
        (error as Error).message || "Failed to check in member",
        defaultToastOptions,
      );
    }
  };

  const handleCheckinAll = () => {
    if (!calendarItemData.getCalendarItem) {
      toast.error("Please select a class", defaultToastOptions);
      return;
    }

    if (
      isMainCoachCheckedIn &&
      isAssistantCoachCheckedIn &&
      registrations.attendees.length === 0
    ) {
      return;
    }

    if (isMainCoachCheckedIn) {
      handleCoachCheckin(calendarItem.mainCoach.userUuid);
    }

    if (isAssistantCoachCheckedIn) {
      handleCoachCheckin(calendarItem.assistantCoach.userUuid);
    }

    try {
      registrations.attendees.forEach((member) => handleCheckin(member));
      registrations.waitlist.forEach((member) => handleCheckin(member));
      toast.success(
        `Successfully checked in ${registrations.attendees.length} attendees and ${registrations.waitlist.length} waitlist members`,
        defaultToastOptions,
      );
    } catch (error) {
      toast.error(
        (error as Error).message || "Failed to check in members",
        defaultToastOptions,
      );
    }
  };

  const handleCoachCheckin = async (userUuid: string) => {
    if (calendarItemData.getCalendarItem) {
      await coachCheckInMutation({
        variables: {
          coachCheckinInput: {
            calendarItemUuid: calendarItemData.getCalendarItem.uuid,
            coachUserUuid: userUuid,
            source: DashboardCheckinSource,
          },
        },
      });
      toast.success(`Coach checked in!`, defaultToastOptions);
      await refetch();
    }
  };

  const handleUndoCheckin = async (member: SelectedMember) => {
    if (!member.checkinUuid) {
      toast.error("No check-in found for this member", defaultToastOptions);
      return;
    }

    try {
      await cancelCheckInMutation({
        variables: {
          cancelCheckinInput: {
            clientUuid: client.uuid,
            checkinUuid: member.checkinUuid,
          },
        },
      });

      await refetch();

      toast.success(
        `Successfully undid check-in for ${member.firstName} ${member.lastName}`,
        defaultToastOptions,
      );
    } catch (error) {
      toast.error(
        (error as Error).message || "Failed to undo check-in",
        defaultToastOptions,
      );
    }
  };

  const handleRemoveMember = async (member: SelectedMember) => {
    try {
      // If member is checked in, we need to undo check-in first
      if (member.status === "checkedin" && member.checkinUuid) {
        await cancelCheckInMutation({
          variables: {
            cancelCheckinInput: {
              clientUuid: client.uuid,
              checkinUuid: member.checkinUuid,
            },
          },
        });
        await removeLateCancelMutation({
          variables: {
            removeLateCancelInput: {
              clientUuid: client.uuid,
              reservationId: member.registrationUuid,
              isLateCancel: false,
            },
          },
        });
      }

      // If they have a registration, cancel it
      if (member.registrationUuid) {
        await cancelReservationMutation({
          variables: {
            cancelReservationInput: {
              clientUuid: client.uuid,
              reservationId: member.registrationUuid,
              isLateCancel: false,
            },
          },
        });
      }

      // Refetch calendar item to get updated attendees
      await refetch();

      toast.success(
        `Successfully removed ${member.firstName} ${member.lastName}`,
        defaultToastOptions,
      );
    } catch (error) {
      toast.error(
        (error as Error).message || "Failed to remove member",
        defaultToastOptions,
      );
    }
  };

  const selectClassComponent = (
    <div className="tw-space-y-2">
      <div className="tw-flex tw-justify-between tw-items-center">
        <Label htmlFor="class">Select Class</Label>
      </div>
      <Select
        onValueChange={(value) => {
          const selectedClass = todaysClasses.find((cls) => cls.id === value);
          setSelectedClass(selectedClass?.id);
          trackEvent("Check-in Class Selected");
        }}
        value={selectedClass}
      >
        <SelectTrigger id="class">
          <SelectValue placeholder="Select a class" />
        </SelectTrigger>
        <SelectContent>
          {todaysClasses &&
            todaysClasses.length > 0 &&
            todaysClasses.map((cls) => (
              <SelectItem key={cls.id} value={cls.id}>
                {`${cls.title} - ${cls.formattedStartTime} - ${cls.formattedEndTime}`}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="tw-flex tw-flex-col tw-h-[95dvh] tw-max-h-[95dvh] tw-relative tw-px-4 tw-py-2">
      <div className="tw-flex tw-flex-col tw-space-y-4">
        {isMultiClass && selectClassComponent}
        <Separator />
      </div>
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4 tw-mt-4">
        <div className="tw-flex tw-flex-col tw-space-y-2">
          {!selectedClass && (
            <div className="tw-flex tw-flex-col tw-space-y-4">
              <Skeleton className="tw-h-4 tw-w-[100px]" />
              <Skeleton className="tw-h-4 tw-w-[150px]" />
            </div>
          )}
          {selectedClass && (
            <>
              <p className="tw-text-lg">
                {isMultiClass && todaysClass?.title}
                {!isMultiClass && classScheduleItem?.title}
              </p>
              <p className="tw-text-sm tw-text-gray-500">
                {isMultiClass &&
                  `${todaysClass?.formattedStartTime} - ${todaysClass?.formattedEndTime}`}
                {!isMultiClass &&
                  `${classScheduleItem?.formattedStartTime} - ${classScheduleItem?.formattedEndTime}`}
              </p>
            </>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(`/schedule/${selectedClass}`)}
        >
          <ArrowRight className="tw-w-4 tw-h-4" />
        </Button>
      </div>
      <div className="tw-flex tw-flex-col tw-space-y-2 tw-mb-2">
        <Separator />
        <Button
          variant="outline"
          className="tw-w-full"
          onClick={showAddMemberModalActions.on}
        >
          + Add Check-in
        </Button>
        {showCheckinAllButton && (
          <Button
            variant="default"
            className="tw-w-full tw-mt-4"
            onClick={handleCheckinAll}
          >
            Check-in All
          </Button>
        )}
      </div>

      <div className="tw-flex-1 tw-overflow-y-auto">
        <div className="tw-space-y-1 tw-mt-4">
          {/* Coaches */}
          {showCoachesSection && (
            <div>
              <p className="tw-text-gray-500 tw-text-sm tw-mb-2">Coaching</p>
              <div className="tw-space-y-4">
                {calendarItem?.mainCoach && (
                  <CheckinMemberCard
                    isCoach={true}
                    isAssistant={false}
                    isMainCoach={true}
                    handleCheckin={() =>
                      handleCoachCheckin(calendarItem.mainCoach.userUuid)
                    }
                    editLink={`schedule/${calendarItem.uuid}/edit/${calendarItem.uuid}`}
                    coachImage={calendarItem.mainCoach.primaryImage}
                    coachName={`${calendarItem.mainCoach.firstName} ${calendarItem.mainCoach.lastName}`}
                    coachStatus={isMainCoachCheckedIn}
                  />
                )}
                {calendarItem?.assistantCoach && (
                  <CheckinMemberCard
                    isCoach={true}
                    isAssistant={true}
                    isMainCoach={false}
                    handleCheckin={() =>
                      handleCoachCheckin(calendarItem.assistantCoach.userUuid)
                    }
                    editLink={`schedule/${calendarItem.uuid}/edit/${calendarItem.uuid}`}
                    coachImage={calendarItem.assistantCoach.primaryImage}
                    coachName={`${calendarItem.assistantCoach.firstName} ${calendarItem.assistantCoach.lastName}`}
                    coachStatus={isAssistantCoachCheckedIn}
                  />
                )}
              </div>
            </div>
          )}
          {/* Waitlist */}
          {showWaitlistSection && (
            <div className="tw-mt-4">
              {showCoachesSection && <Separator />}
              <p className="tw-text-gray-500 tw-text-sm tw-mt-4 tw-mb-2">
                Waitlist
              </p>
              <div className="tw-space-y-4">
                {registrations.waitlist.map((member) => (
                  <CheckinMemberCard
                    key={member.userUuid}
                    member={member}
                    handleCheckin={() => handleCheckin(member)}
                    handleUndoCheckin={() => handleUndoCheckin(member)}
                    handleRemove={() => handleRemoveMember(member)}
                  />
                ))}
              </div>
            </div>
          )}
          {/* Attendees */}
          {showAttendeesSection && (
            <div className="tw-mt-4">
              {showCoachesSection && <Separator />}
              <p className="tw-text-gray-500 tw-text-sm tw-mt-4 tw-mb-2">
                Attending
              </p>
              <div className="tw-space-y-4">
                {registrations.attendees.map((member) => (
                  <CheckinMemberCard
                    key={member.userUuid}
                    member={member}
                    handleCheckin={() => handleCheckin(member)}
                    handleUndoCheckin={() => handleUndoCheckin(member)}
                    handleRemove={() => handleRemoveMember(member)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <AddMemberModal
        noPortal={true}
        useDialog={true}
        visible={showAddMemberModal}
        onClose={showAddMemberModalActions.off}
        onSuccess={() => {
          showAddMemberModalActions.off();
          refetch();
        }}
        calendarUuid={selectedClass}
      />
    </div>
  );
};
